import axios from "axios";
import Config from "config";
import { ThunkAction } from "redux-thunk";
import { AnyAction } from "redux";
import { toast } from "react-toastify";

// Types
export interface ParentChain {
  id: number;
  name: string;
  channel: string;
  logo?: string;
  numberOfChains: number,
  publishedProjects: number,
  chain_count?: number;
  published_projects?: number;
  sasurl?:string
}

export interface ParentChainsState {
  loading: boolean;
  error: string | null;
  content: ParentChain[];
  page: number;
  limit: number;
  totalPages: number;
  totalItems: number;
  currentPage: number;
  chain_count?: number;
  published_projects?: number;
  sasurl:string
}

// Initial State
const initialState: ParentChainsState = {
  loading: false,
  error: null,
  content: [],
  page: 0,
  limit: 0,
  totalPages: 0,
  totalItems: 0,
  currentPage: 0,
  sasurl:""
};

// Action Types
const ADD_PARENT_CHAINS_REQUEST = "ADD_PARENT_CHAINS_REQUEST";
const ADD_PARENT_CHAINS_SUCCESS = "ADD_PARENT_CHAINS_SUCCESS";
const ADD_PARENT_CHAINS_FAILURE = "ADD_PARENT_CHAINS_FAILURE";

const FETCH_PARENT_CHAINS_REQUEST = "FETCH_PARENT_CHAINS_REQUEST";
const FETCH_PARENT_CHAINS_SUCCESS = "FETCH_PARENT_CHAINS_SUCCESS";
const FETCH_PARENT_CHAINS_FAILURE = "FETCH_PARENT_CHAINS_FAILURE";

const UPDATE_PARENT_CHAIN_REQUEST = "UPDATE_PARENT_CHAIN_REQUEST";
const UPDATE_PARENT_CHAIN_SUCCESS = "UPDATE_PARENT_CHAIN_SUCCESS";
const UPDATE_PARENT_CHAIN_FAILURE = "UPDATE_PARENT_CHAIN_FAILURE";

const DELETE_PARENT_CHAIN_REQUEST = "DELETE_PARENT_CHAIN_REQUEST";
const DELETE_PARENT_CHAIN_SUCCESS = "DELETE_PARENT_CHAIN_SUCCESS";
const DELETE_PARENT_CHAIN_FAILURE = "DELETE_PARENT_CHAIN_FAILURE";

const CHAIN_PARENT_LOGO_SAS_URL_REQUEST = "CHAIN_PARENT_LOGO_SAS_URL_REQUEST"
const CHAIN_PARENT_LOGO_SAS_URL_SUCCESS = "CHAIN_PARENT_LOGO_SAS_URL_SUCCESS"
const CHAIN_PARENT_LOGO_SAS_URL_FAILURE = "CHAIN_PARENT_LOGO_SAS_URL_FAILURE"

// Reducer
export default function parentChainsReducer(
  state = initialState,
  action: AnyAction
): ParentChainsState {
  switch (action.type) {
    case ADD_PARENT_CHAINS_SUCCESS:
      return {
        ...state,
        loading: false,
        content: [...state.content, action.payload],
      };

    case FETCH_PARENT_CHAINS_REQUEST:
    case UPDATE_PARENT_CHAIN_REQUEST:
    case DELETE_PARENT_CHAIN_REQUEST:
      return { ...state, loading: true, error: null };

    case FETCH_PARENT_CHAINS_SUCCESS:
      return {
        ...state,
        loading: false,
        content: action.payload.content,
        totalItems: action.payload.totalItems,
        totalPages: action.payload.totalPages,
        currentPage: action.payload.currentPage
      };

    case UPDATE_PARENT_CHAIN_SUCCESS:
      return {
        ...state,
        loading: false,
        content: state.content.map((chain) =>
          chain.id === action.payload.id ? action.payload : chain
        ),
      };

    case DELETE_PARENT_CHAIN_SUCCESS:
      return {
        ...state,
        loading: false,
        content: state.content.filter((chain) => chain.id !== action.payload),
      };

    case FETCH_PARENT_CHAINS_FAILURE:
    case UPDATE_PARENT_CHAIN_FAILURE:
    case DELETE_PARENT_CHAIN_FAILURE:
      return { ...state, loading: false, error: action.error };

    default:
      return state;
  }
}

// Thunk Actions
// Add a Parent Chain
export const addParentChain = (
  data: FormData
): ThunkAction<Promise<void>, { parentChains: ParentChainsState }, unknown, AnyAction> =>
  async (dispatch) => {
    dispatch({ type: ADD_PARENT_CHAINS_REQUEST });
    try {
      const response = await axios.post(
        `${Config.API_SERVICE_URL}/api/parentChain/addParentChain`,
        data
      );
      dispatch({ type: ADD_PARENT_CHAINS_SUCCESS, payload: response.data });
    } catch (error: any) {
      dispatch({
        type: ADD_PARENT_CHAINS_FAILURE,
        error: error.message || "Error adding parent chain",
      });
      throw error;
    }
  };

// Fetch All Parent Chains
export const fetchParentChains = (page: number = 1, limit: number = 10): ThunkAction<
  Promise<void>,
  { parentChains: ParentChainsState },
  unknown,
  AnyAction
> => async (dispatch) => {
  dispatch({ type: FETCH_PARENT_CHAINS_REQUEST });
  try {
    const response = await axios.get(`${Config.API_SERVICE_URL}/api/parentChain/getParentChain?page=${page}&limit=${limit}`);
    dispatch({
      type: FETCH_PARENT_CHAINS_SUCCESS, payload: {
        content: response.data.chains,
        totalItems: response.data.pagination.totalItems,
        totalPages: response.data.pagination.totalPages,
        currentPage: response.data.pagination.currentPage
      }
    });
  } catch (error: any) {
    dispatch({ type: FETCH_PARENT_CHAINS_FAILURE, error: error.message || "Error fetching chains" });
  }
};

// Update a Parent Chain
export const updateParentChain = (
  id: number,
  data: FormData
): ThunkAction<Promise<void>, { parentChains: ParentChainsState }, unknown, AnyAction> =>
  async (dispatch) => {
    dispatch({ type: UPDATE_PARENT_CHAIN_REQUEST });
    try {
      const response = await axios.patch(
        `${Config.API_SERVICE_URL}/api/parentChain/updateParentChain/${id}`,
        data
      );
      dispatch({ type: UPDATE_PARENT_CHAIN_SUCCESS, payload: response.data });
    } catch (error: any) {
      dispatch({
        type: UPDATE_PARENT_CHAIN_FAILURE,
        error: error.message || "Error updating chain",
      });
    }
  };

// // Delete a Parent Chain
export const deleteParentChain = (
  id: number
): ThunkAction<Promise<void>, { parentChains: ParentChainsState }, unknown, AnyAction> =>
  async (dispatch) => {
    dispatch({ type: DELETE_PARENT_CHAIN_REQUEST });
    try {
      await axios.delete(`${Config.API_SERVICE_URL}/api/parentChain/${id}`);
      dispatch({ type: DELETE_PARENT_CHAIN_SUCCESS, payload: id });
    } catch (error: any) {
      console.log("error=>",error)
      if (error.response?.status === 409) {
        dispatch({
          type: DELETE_PARENT_CHAIN_FAILURE,
          error: error.response || "This parent chain cannot be deleted because it is in use.",
        });
        // toast.warning("This parent chain cannot be deleted because it is in use.");
        // return;
      }
      dispatch({
        type: DELETE_PARENT_CHAIN_FAILURE,
        error: error.message || "Error deleting chain",
      });
    }
  };

export const getChainParentLogoSASURL = (
  blobName: string
): ThunkAction<Promise<void>, { parentChains: ParentChainsState }, unknown, AnyAction> =>
  async (dispatch) => {
    dispatch({ type: CHAIN_PARENT_LOGO_SAS_URL_REQUEST });
    try {
      const response = await axios.post(`${Config.API_SERVICE_URL}/api/parentChain/getUIsasurl`,blobName);
      dispatch({ type: CHAIN_PARENT_LOGO_SAS_URL_SUCCESS, payload: response.data });
    } catch (error: any) {
      console.log("error=>",error)
      if (error.response?.status === 409) {
        dispatch({
          type: CHAIN_PARENT_LOGO_SAS_URL_FAILURE,
          error: error.response || "This parent chain logo not in use",
        });
        // toast.warning("This parent chain cannot be deleted because it is in use.");
        // return;
      }
      dispatch({
        type: CHAIN_PARENT_LOGO_SAS_URL_FAILURE,
        error: error.message || "Error deleting chain",
      });
    }
  };

